import { useState } from "react";
import { trackEvent } from "@/utils/lawnStrikeTracker";
import { isFounder } from "@/utils/founderAccess";

// ---------------------------------------------------------------
// /activate-node — 3-Tier Node Selection ladder (Basic /
// Saturation / Intelligence). Two-step gate — Digital Handshake
// (7-hour commitment ack) + Utility Registration form (email +
// company) — then the operator picks a tier and gets punched
// straight into Stripe Checkout via POST /api/stripe/create-checkout.
//
// Aesthetic mirrors /roundtable Executive Summit: Playfair Display
// serif headings, rich black canvas, warm-gold accents.
// ---------------------------------------------------------------

const GOLD = "#C9A84C";
const GOLD_SOFT = "rgba(201,168,76,0.55)";
const GOLD_INK = "rgba(201,168,76,0.18)";
const INK = "#0A0A0A";
const CARD = "#131313";
const HAIRLINE = "rgba(201,168,76,0.22)";
const MUTED = "#B7B7B0";
const RED = "#EF4444";
const IVORY = "#F5F1E3";
const API = process.env.REACT_APP_BACKEND_URL;

const SERIF = `'Playfair Display', 'Cormorant Garamond', Georgia, serif`;
const SANS = `'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif`;

// Tier catalogue — kept in one place so the card list, telemetry,
// and Stripe payload all agree. Stripe price IDs live server-side;
// the frontend only sends the tier key.
const TIERS = [
  {
    key: "basic",
    name: "Basic Node",
    price: "$97",
    tagline: "Entry to The Grid",
    style: "ivory", // white/ivory card
    features: [
      "24/7 AI Intake (Amanda)",
      "Lead Shield — no missed rings",
      "Basic Call Routing",
    ],
  },
  {
    key: "saturation",
    name: "Saturation Node",
    price: "$297",
    tagline: "Full Utility · Operator Standard",
    style: "gold", // highlighted gold card
    featured: true,
    features: [
      "Everything in Basic Node",
      "Intake Shield · Command Calendar",
      "Billing Autopilot · Growth Engine",
    ],
  },
  {
    key: "intelligence",
    name: "Intelligence Node",
    price: "$497",
    tagline: "Full Utility + SLOAN Intelligence",
    style: "obsidian", // premium black card
    features: [
      "Everything in Saturation Node",
      "SLOAN Technical Intelligence",
      "Plant ID · Equipment Library · Tech Protocols",
    ],
  },
];

export default function ActivateNode() {
  const founder = isFounder();
  const [handshakeConfirmed, setHandshakeConfirmed] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    company: "",
  });
  const [loadingTier, setLoadingTier] = useState(null);
  const [error, setError] = useState("");

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const formValid =
    handshakeConfirmed &&
    form.full_name.trim() &&
    form.email.trim() &&
    form.phone.trim() &&
    form.company.trim();

  const activateTier = async (tierKey) => {
    if (!formValid || loadingTier) return;
    // Founders never pay for the Intelligence tier — the button
    // is masked in the UI, but guard here too so a stray call
    // can't accidentally kick off a Stripe session.
    if (founder && tierKey === "intelligence") return;
    setError("");
    setLoadingTier(tierKey);

    trackEvent("NODE_ACTIVATION_SUBMITTED", {
      tier: tierKey,
      handshake_confirmed: true,
      full_name: form.full_name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      company: form.company.trim(),
    });

    try {
      const res = await fetch(`${API}/api/stripe/create-checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tier: tierKey,
          customerEmail: form.email.trim(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.checkoutUrl) {
        throw new Error(
          data.detail || data.message || `Checkout unavailable (HTTP ${res.status})`,
        );
      }
      window.location.href = data.checkoutUrl;
    } catch (err) {
      console.error("[ActivateNode] Tier checkout failed:", err);
      setError(
        (err && err.message) ||
          "We couldn't reach Stripe just now. Try again shortly.",
      );
      setLoadingTier(null);
    }
  };

  return (
    <div
      data-testid="activate-node-root"
      style={{
        minHeight: "100vh",
        background: INK,
        color: IVORY,
        fontFamily: SANS,
      }}
    >
      <style>{`
        @keyframes an-fade {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .an-fade  { animation: an-fade 900ms ease-out both; }
        .an-fade2 { animation: an-fade 900ms ease-out both; animation-delay: 140ms; }
        .an-fade3 { animation: an-fade 900ms ease-out both; animation-delay: 280ms; }

        .an-tier { transition: transform 260ms ease, box-shadow 260ms ease, border-color 260ms ease; }
        .an-tier:hover { transform: translateY(-6px); }
        .an-tier.ivory:hover     { box-shadow: 0 30px 60px -25px rgba(245,241,227,0.35); }
        .an-tier.gold:hover      { box-shadow: 0 40px 80px -25px rgba(201,168,76,0.55); }
        .an-tier.obsidian:hover  { box-shadow: 0 30px 60px -25px rgba(201,168,76,0.35); }

        .an-cta { transition: transform 220ms ease, box-shadow 220ms ease, opacity 220ms ease; }
        .an-cta:not(:disabled):hover { transform: translateY(-1px); }

        .an-input:focus { border-color: ${GOLD} !important; box-shadow: 0 0 0 3px rgba(201,168,76,0.20); }

        @media (max-width: 720px) {
          .an-hero-title { font-size: 2.4rem !important; }
        }
      `}</style>

      {/* ============ HERO ============ */}
      <section
        style={{
          padding: "88px 24px 56px",
          textAlign: "center",
          borderBottom: `1px solid ${HAIRLINE}`,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(60% 60% at 50% 20%, rgba(201,168,76,0.10), rgba(0,0,0,0) 70%)",
            pointerEvents: "none",
          }}
        />
        <div
          className="an-fade"
          data-testid="activate-node-kicker"
          style={{
            display: "inline-block",
            padding: "8px 18px",
            border: `1px solid ${GOLD_SOFT}`,
            color: GOLD,
            fontSize: 11,
            letterSpacing: "0.42em",
            textTransform: "uppercase",
            fontWeight: 600,
            marginBottom: 24,
            borderRadius: 999,
          }}
        >
          Priority Node · Activation
        </div>
        <h1
          className="an-fade2 an-hero-title"
          data-testid="activate-node-headline"
          style={{
            fontFamily: SERIF,
            fontWeight: 700,
            fontSize: "clamp(2.4rem, 5.4vw, 4.2rem)",
            lineHeight: 1.05,
            letterSpacing: "-0.01em",
            margin: 0,
            color: IVORY,
          }}
        >
          Activate Your Command Center
        </h1>
        <p
          className="an-fade3"
          data-testid="activate-node-subtitle"
          style={{
            marginTop: 18,
            fontFamily: SERIF,
            fontStyle: "italic",
            fontSize: "clamp(1rem, 1.5vw, 1.2rem)",
            color: GOLD,
            letterSpacing: "0.12em",
          }}
        >
          Priority Node Selection &nbsp;•&nbsp; Standard Utility Terms
        </p>

        {founder && (
          <div
            className="an-fade3"
            data-testid="activate-node-founder-badge"
            style={{
              margin: "32px auto 0",
              maxWidth: 560,
              background: "linear-gradient(160deg, rgba(201,168,76,0.16) 0%, rgba(10,10,10,0.6) 90%)",
              border: `1px solid ${GOLD}`,
              borderRadius: 12,
              padding: "14px 20px",
              display: "flex",
              alignItems: "center",
              gap: 12,
              boxShadow: `0 24px 60px -30px rgba(201,168,76,0.55)`,
              textAlign: "left",
            }}
          >
            <span
              aria-hidden
              style={{
                width: 34, height: 34, borderRadius: "50%",
                background: GOLD, color: "#0A0A0A",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 18, fontWeight: 900, flexShrink: 0,
              }}
            >
              ★
            </span>
            <div>
              <div style={{ color: GOLD, fontSize: 10, letterSpacing: "0.36em", textTransform: "uppercase", fontWeight: 700 }}>
                Founder Access Granted
              </div>
              <div style={{ color: IVORY, fontSize: "0.95rem", marginTop: 2 }}>
                Intelligence Tier already included · no purchase required
              </div>
            </div>
          </div>
        )}
      </section>

      {/* ============ GATE : Handshake + Registration ============ */}
      <section style={{ maxWidth: 880, margin: "0 auto", padding: "56px 24px 24px" }}>
        {/* Step 1 · Digital Handshake */}
        <div
          data-testid="activate-node-step-handshake"
          style={{
            background: CARD,
            border: `1px solid ${handshakeConfirmed ? GOLD : HAIRLINE}`,
            borderRadius: 14,
            padding: "28px 28px",
            transition: "border-color 240ms ease",
          }}
        >
          <div
            style={{
              color: GOLD,
              fontSize: 11,
              letterSpacing: "0.3em",
              textTransform: "uppercase",
              fontWeight: 700,
              marginBottom: 10,
            }}
          >
            Step 01
          </div>
          <h2 style={{ fontFamily: SERIF, color: IVORY, fontSize: "1.7rem", margin: "0 0 12px", fontWeight: 700 }}>
            The Digital Handshake
          </h2>
          <p style={{ color: MUTED, margin: "0 0 22px", lineHeight: 1.6 }}>
            The Grid works because operators show up. Confirm a{" "}
            <span style={{ color: IVORY, fontWeight: 600 }}>7-hour weekly commitment</span>{" "}
            to running your Command Center — reviewing intake, closing the calendar, staying on the mic with your crew.
          </p>
          <label
            htmlFor="handshake-check"
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 14,
              cursor: "pointer",
              background: INK,
              border: `1px solid ${handshakeConfirmed ? GOLD : HAIRLINE}`,
              borderRadius: 10,
              padding: "16px 18px",
              transition: "all 240ms ease",
              boxShadow: handshakeConfirmed
                ? `0 0 0 3px rgba(201,168,76,0.14), inset 0 0 20px rgba(201,168,76,0.14)`
                : "none",
            }}
          >
            <input
              id="handshake-check"
              type="checkbox"
              data-testid="activate-node-handshake-checkbox"
              checked={handshakeConfirmed}
              onChange={(e) => setHandshakeConfirmed(e.target.checked)}
              style={{
                marginTop: 3,
                width: 20,
                height: 20,
                accentColor: GOLD,
                cursor: "pointer",
                flexShrink: 0,
              }}
            />
            <span style={{ color: IVORY, fontSize: "0.98rem", lineHeight: 1.5 }}>
              I confirm the{" "}
              <span style={{ color: GOLD, fontWeight: 700 }}>7-hour weekly commitment</span>{" "}
              to operate my Command Center and honor the Standard Utility Terms.
            </span>
          </label>
        </div>

        {/* Step 2 · Utility Registration */}
        <div
          data-testid="activate-node-step-registration"
          style={{
            marginTop: 22,
            background: CARD,
            border: `1px solid ${HAIRLINE}`,
            borderRadius: 14,
            padding: "28px 28px",
            opacity: handshakeConfirmed ? 1 : 0.55,
            transition: "opacity 240ms ease",
          }}
        >
          <div
            style={{
              color: GOLD,
              fontSize: 11,
              letterSpacing: "0.3em",
              textTransform: "uppercase",
              fontWeight: 700,
              marginBottom: 10,
            }}
          >
            Step 02
          </div>
          <h2 style={{ fontFamily: SERIF, color: IVORY, fontSize: "1.7rem", margin: "0 0 12px", fontWeight: 700 }}>
            Utility Registration
          </h2>
          <p style={{ color: MUTED, margin: "0 0 22px", lineHeight: 1.6 }}>
            The billing profile Amanda uses to onboard your crew. Encrypted in transit — unlocks after the handshake above.
          </p>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
              gap: 14,
            }}
          >
            <Field label="Full Name" testid="activate-node-full-name" value={form.full_name} onChange={setField("full_name")} disabled={!handshakeConfirmed || !!loadingTier} placeholder="John Vance" />
            <Field label="Business Email" testid="activate-node-email" type="email" value={form.email} onChange={setField("email")} disabled={!handshakeConfirmed || !!loadingTier} placeholder="john@yourcompany.com" />
            <Field label="Phone" testid="activate-node-phone" type="tel" value={form.phone} onChange={setField("phone")} disabled={!handshakeConfirmed || !!loadingTier} placeholder="(555) 555-0134" />
            <Field label="Company" testid="activate-node-company" value={form.company} onChange={setField("company")} disabled={!handshakeConfirmed || !!loadingTier} placeholder="Acme Landscaping" />
          </div>
        </div>
      </section>

      {/* ============ 3-TIER NODE LADDER ============ */}
      <section style={{ maxWidth: 1240, margin: "0 auto", padding: "56px 24px 40px" }}>
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              color: GOLD,
              fontSize: 11,
              letterSpacing: "0.4em",
              textTransform: "uppercase",
              fontWeight: 600,
            }}
          >
            Step 03 · Node Selection
          </div>
          <h2
            style={{
              fontFamily: SERIF,
              color: IVORY,
              fontSize: "clamp(1.8rem, 3.4vw, 2.6rem)",
              fontWeight: 700,
              margin: "12px 0 12px",
              letterSpacing: "-0.005em",
            }}
          >
            Choose your node.
          </h2>
          <p style={{ color: MUTED, maxWidth: 640, margin: "0 auto", lineHeight: 1.55 }}>
            Three activation tiers. Pick the level of Grid presence that matches
            your operation — you can always graduate up later.
          </p>
        </div>

        <div
          data-testid="activate-node-tiers"
          style={{
            marginTop: 44,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: 22,
            alignItems: "stretch",
          }}
        >
          {TIERS.map((t) => (
            <TierCard
              key={t.key}
              tier={t}
              disabled={!formValid || !!loadingTier}
              loading={loadingTier === t.key}
              onActivate={() => activateTier(t.key)}
              founder={founder}
            />
          ))}
        </div>

        {error && (
          <div
            data-testid="activate-node-error"
            style={{
              marginTop: 26,
              background: "linear-gradient(160deg, rgba(239,68,68,0.10), rgba(10,10,10,0.9))",
              border: `1px solid ${RED}88`,
              borderRadius: 10,
              padding: "16px 20px",
              maxWidth: 780,
              margin: "26px auto 0",
              color: IVORY,
              fontSize: "0.98rem",
            }}
          >
            <div style={{ color: RED, fontSize: 10, letterSpacing: "0.3em", textTransform: "uppercase", fontWeight: 700, marginBottom: 6 }}>
              Activation Failed
            </div>
            {error}
          </div>
        )}

        {!formValid && (
          <div
            data-testid="activate-node-gate-hint"
            style={{
              marginTop: 28,
              textAlign: "center",
              color: MUTED,
              fontFamily: SERIF,
              fontStyle: "italic",
              fontSize: "0.98rem",
            }}
          >
            Complete the handshake + utility registration above to unlock tier activation.
          </div>
        )}
      </section>

      {/* ============ FOOTER ============ */}
      <footer
        style={{
          marginTop: 40,
          padding: "28px 24px 40px",
          borderTop: `1px solid ${HAIRLINE}`,
          textAlign: "center",
        }}
      >
        <div style={{ color: GOLD, fontFamily: SERIF, fontStyle: "italic", fontSize: "0.9rem", letterSpacing: "0.06em" }}>
          © Wingman Alfred &nbsp;•&nbsp; Priority Node Activation
        </div>
      </footer>
    </div>
  );
}

// ---- Field --------------------------------------------------
function Field({ label, testid, value, onChange, disabled, placeholder, type = "text" }) {
  return (
    <label
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        color: MUTED,
        fontSize: 11,
        letterSpacing: "0.26em",
        textTransform: "uppercase",
        fontWeight: 700,
      }}
    >
      {label}
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        data-testid={testid}
        className="an-input"
        style={{
          background: INK,
          color: IVORY,
          border: `1px solid ${HAIRLINE}`,
          borderRadius: 8,
          padding: "13px 15px",
          fontSize: "1em",
          outline: "none",
          fontFamily: SANS,
          letterSpacing: "normal",
          textTransform: "none",
          fontWeight: 400,
          opacity: disabled ? 0.55 : 1,
          transition: "border-color 200ms ease, box-shadow 200ms ease",
        }}
      />
    </label>
  );
}

// ---- Tier Card ----------------------------------------------
function TierCard({ tier, disabled, loading, onActivate, founder }) {
  const isIvory = tier.style === "ivory";
  const isGold = tier.style === "gold";
  const isObsidian = tier.style === "obsidian";
  const founderUnlocked = founder && tier.key === "intelligence";

  // Style variants per tier — the card body colour, text colour,
  // and check-mark hue all shift so the ladder reads as a real
  // progression rather than three identical cards.
  const bg = isIvory
    ? "linear-gradient(160deg, #F5F1E3 0%, #E9E2CB 100%)"
    : isGold
    ? `linear-gradient(160deg, ${GOLD} 0%, #B4923D 100%)`
    : `linear-gradient(160deg, #0F0F0F 0%, #050505 100%)`;
  const borderColor = isIvory ? "rgba(201,168,76,0.35)" : isGold ? "#B4923D" : GOLD;
  const textColor = isIvory || isGold ? "#111" : IVORY;
  const mutedColor = isIvory || isGold ? "rgba(0,0,0,0.65)" : MUTED;
  const accentColor = isIvory || isGold ? "#5a4600" : GOLD;
  const checkColor = isIvory ? "#4a3800" : isGold ? "#2a1e00" : GOLD;
  const btnBg = isIvory || isGold ? "#0A0A0A" : GOLD;
  const btnColor = isIvory || isGold ? GOLD : "#0A0A0A";

  return (
    <div
      className={`an-tier ${tier.style}`}
      data-testid={`activate-node-tier-${tier.key}`}
      style={{
        background: bg,
        border: `1px solid ${borderColor}`,
        borderRadius: 16,
        padding: "30px 26px",
        color: textColor,
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "hidden",
        boxShadow: isGold
          ? `0 24px 60px -20px rgba(201,168,76,0.45), inset 0 1px 0 rgba(255,255,255,0.15)`
          : `0 20px 44px -22px rgba(0,0,0,0.6)`,
        transform: isGold ? "translateY(-6px)" : "none",
      }}
    >
      {tier.featured && (
        <div
          style={{
            position: "absolute",
            top: 14,
            right: 14,
            background: "#0A0A0A",
            color: GOLD,
            fontFamily: SANS,
            fontSize: 10,
            letterSpacing: "0.24em",
            textTransform: "uppercase",
            fontWeight: 700,
            padding: "6px 10px",
            borderRadius: 999,
            border: `1px solid ${GOLD}`,
          }}
          data-testid="tier-featured-badge"
        >
          Most Chosen
        </div>
      )}

      <div
        style={{
          color: accentColor,
          fontSize: 11,
          letterSpacing: "0.36em",
          textTransform: "uppercase",
          fontWeight: 700,
          marginBottom: 8,
        }}
      >
        {tier.tagline}
      </div>
      <h3
        style={{
          fontFamily: SERIF,
          fontSize: "1.9rem",
          fontWeight: 700,
          margin: 0,
          color: textColor,
          letterSpacing: "-0.005em",
        }}
      >
        {tier.name}
      </h3>
      <div
        style={{
          marginTop: 14,
          display: "flex",
          alignItems: "baseline",
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <span
          style={{
            fontFamily: SERIF,
            fontSize: "3.2rem",
            fontWeight: 700,
            lineHeight: 1,
            color: textColor,
            textDecoration: founderUnlocked ? "line-through" : "none",
            opacity: founderUnlocked ? 0.55 : 1,
          }}
          data-testid={`tier-${tier.key}-price`}
        >
          {tier.price}
        </span>
        {!founderUnlocked && (
          <span style={{ color: mutedColor, fontSize: "1rem", fontWeight: 500 }}>
            /mo
          </span>
        )}
        {founderUnlocked && (
          <span
            data-testid={`tier-${tier.key}-included`}
            style={{
              color: accentColor,
              fontFamily: SANS,
              fontSize: "0.72rem",
              fontWeight: 800,
              letterSpacing: "0.28em",
              textTransform: "uppercase",
              background: "rgba(0,0,0,0.35)",
              border: `1px solid ${accentColor}`,
              padding: "5px 10px",
              borderRadius: 999,
              alignSelf: "center",
            }}
          >
            Included
          </span>
        )}
      </div>

      <ul
        style={{
          listStyle: "none",
          padding: 0,
          margin: "24px 0 28px",
          display: "grid",
          gap: 10,
        }}
      >
        {tier.features.map((f) => (
          <li
            key={f}
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 10,
              color: textColor,
              fontSize: "0.98rem",
              lineHeight: 1.5,
            }}
          >
            <span
              aria-hidden
              style={{
                color: checkColor,
                fontWeight: 800,
                fontSize: "1.05em",
                lineHeight: 1.5,
                flexShrink: 0,
              }}
            >
              ✓
            </span>
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <button
        type="button"
        className="an-cta"
        data-testid={`activate-node-cta-${tier.key}`}
        disabled={founderUnlocked || disabled || loading}
        onClick={founderUnlocked ? undefined : onActivate}
        style={{
          marginTop: "auto",
          background: btnBg,
          color: btnColor,
          padding: "15px 24px",
          fontSize: "0.9rem",
          border: `1px solid ${isObsidian ? GOLD : "transparent"}`,
          borderRadius: 10,
          cursor: founderUnlocked ? "default" : (disabled || loading ? "not-allowed" : "pointer"),
          fontFamily: SANS,
          fontWeight: 800,
          letterSpacing: "0.24em",
          textTransform: "uppercase",
          opacity: founderUnlocked ? 1 : (disabled && !loading ? 0.6 : 1),
          boxShadow: isObsidian
            ? `0 14px 30px -14px ${GOLD}`
            : `0 12px 24px -12px rgba(0,0,0,0.5)`,
        }}
      >
        {founderUnlocked
          ? "★ Included with Founder Access"
          : loading
          ? "Redirecting to Stripe…"
          : "Activate This Node"}
      </button>
    </div>
  );
}
